import React, { useState, useEffect, useCallback } from 'react';
import { getRequestOptions } from '../helpers/Fetchwrapper';
import { KTSVG } from '../../_metronic/helpers';
import { Col, Row, Table } from 'react-bootstrap-v5';
import 'react-dropzone-uploader/dist/styles.css'
import Select from 'react-select';
import { Link } from 'react-router-dom'
import { numberFormat } from '../helpers/GlobalHelper';

interface Item {
    id_items: number;
    itemName: string;
    itemCode: string;
    stock: number;
    total_stock_in: number;
    total_stock_out: number;
    average_rate: number;
    latest_purchase_rate: number;
    alternateName?: string;
    similar_item_names?: string;
    unit: { unitName: string };
    item_category?: { categoryName: string };
}

interface Product {
    id_products: number;
    productName: string;
    packing: string;
    size: string;
    categoryName: string;
    stock: number;
    total_stock_in: number;
    total_stock_out: number;
    average_rate: number;
    latest_production_rate: number;
    alternateName?: string;
    unit?: { unitName: string };
}


interface Column {
    header: string;
    accessor: string;
    render?: (data: Item | Product) => React.ReactNode;
}

// Reusable Stock Table Component
const StockTable: React.FC<{ data: Item[] | Product[]; columns: Column[]; title: string }> = ({ data, columns, title }) => (
    <Col md={12}>
        <h4>{title}</h4>
        <Table striped bordered hover size="sm">
            <thead>
                <tr>
                    {columns.map((col, idx) => <th key={idx}>{col.header}</th>)}
                </tr>
            </thead>
            <tbody>
                {data.map((item, index) => (
                    <tr key={index}>
                        {columns.map((col, idx) => (
                            <td key={idx}>
                                {col.render ? col.render(item) : (item as any)[col.accessor]}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </Table>
    </Col>
);

const InventoryStockStatus: React.FC = () => {

    const [items, setItems] = useState<Item[]>([]);
    const [products, setProducts] = useState<Product[]>([]);

    const [selectProjectOptions, setSelectProjectOptions] = useState([])
    const [selectedProject, setSelectedProject] = useState<{ value: string; label: string; } | null>(null);

    // Warehouse dropdown state
	const [warehouses, setWarehouses] = useState([]);
	const [warehouse, setWarehouse] = useState({ label: 'Select Warehouse', value: '' });

    // Item Category dropdown state
    const [itemCategories, setItemCategories] = useState<Array<{label: string, value: string}>>([]);
    const [selectedItemCategory, setSelectedItemCategory] = useState({ label: 'All Item Categories', value: '' });

    // Product Category dropdown state
    const [productCategories, setProductCategories] = useState<Array<{label: string, value: string}>>([]);
    const [selectedProductCategory, setSelectedProductCategory] = useState({ label: 'All Product Categories', value: '' });

    // Search state
    const [searchText, setSearchText] = useState('');

    // Show zero stock state
    const [showZeroStock, setShowZeroStock] = useState(true);


    const getProjectList = async () => {
        const url = process.env.REACT_APP_API_URL + 'projects';

        const response = await fetch(url, getRequestOptions());

        const data = await response.json();

        data?.data?.map((element: any) => {
            element.value = element.id_projects;
            element.label = element.project_name;
            return element;
        })

        setSelectProjectOptions(data.data);
    }

    // Fetch Item Categories
    const getItemCategories = useCallback(async () => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + 'allItemCategories', getRequestOptions());
            const data = await response.json();
            
            if (data && Array.isArray(data)) {
                const categoryOptions = data.map((item: any) => ({
                    label: item.category_name,
                    value: item.id_item_categories
                }));
                
                // Add "All" option at the beginning
                const allOption = { label: 'All Item Categories', value: '' };
                setItemCategories([allOption, ...categoryOptions]);
            }
        } catch (error) {
            console.error('Error fetching item categories:', error);
        }
    }, []);

    // Fetch Product Categories
    const getProductCategories = useCallback(async () => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + 'product-categories?id_projects='+ (selectedProject?.value ?? ''), getRequestOptions());
            const data = await response.json();
            
            if (data && Array.isArray(data)) {
                const categoryOptions = data.map((item: any) => ({
                    label: item.category_name,
                    value: item.id_product_categories
                }));
                
                // Add "All" option at the beginning
                const allOption = { label: 'All Product Categories', value: '' };
                setProductCategories([allOption, ...categoryOptions]);
            }
        } catch (error) {
            console.error('Error fetching product categories:', error);
        }
    }, [selectedProject]);

    useEffect(() => {
        getProjectList();
        getItemCategories();
        getProductCategories();
    }, [getItemCategories, getProductCategories])


    // Fetch warehouses by project
	const getWarehousesByProject = (idProjects: number) => {
		if (!idProjects) {
			setWarehouses([]);
			setWarehouse({ label: 'Select Warehouse', value: '' });
			return;
		}
		fetch(
			process.env.REACT_APP_API_URL + 'warehouse/' + idProjects + '/list',
			getRequestOptions()
		)
			.then((resp) => resp.json())
			.then((resp) => {
				if (resp.success && Array.isArray(resp.data)) {
					const whOptions = resp.data.map((item: any) => ({
						label: item.name,
						value: item.id_warehouses
					}));
					setWarehouses(whOptions);
				} else {
					setWarehouses([]);
				}
				setWarehouse({ label: 'Select Warehouse', value: '' });
			})
			.catch(() => {
				setWarehouses([]);
				setWarehouse({ label: 'Select Warehouse', value: '' });
			});
	};

    const getStockStatus = useCallback(async () => {
        let idProjects = selectedProject?.value ?? '';
        let idWarehouses = warehouse?.value ?? '';
        let idItemCategory = selectedItemCategory?.value ?? '';
        let idProductCategory = selectedProductCategory?.value ?? '';
        let search = searchText;
        
        try {
            const queryParams = new URLSearchParams({
                id_projects: idProjects,
                id_warehouses: idWarehouses,
                id_item_category: idItemCategory,
                id_product_category: idProductCategory,
                search: search
            });
            
            const response = await fetch(process.env.REACT_APP_API_URL + 'inventory-stock-status?' + queryParams.toString(), getRequestOptions());
            const data = await response.json();
            setItems(data.data.items);
            setProducts(data.data.products);
        } catch (error) {
            console.error('catch the hoop', error);
        }
    }, [selectedProject, warehouse, selectedItemCategory, selectedProductCategory, searchText]);

    // Define columns for items table
    const itemColumns: Column[] = [
        { header: 'SL. No', accessor: 'index' },
        { header: 'Item Name', accessor: 'itemName', render: item => <Link to={'/item-stock-details?id_items=' + (item as Item)?.id_items + '&id_projects=' + (selectedProject?.value ?? '')+'&id_warehouses='+(warehouse.value ?? '')} className="label label-info">
            {(item as Item).itemName}
            <br></br>
			<span style={{ fontSize: '10px' }}>{(item as Item).alternateName}</span>
        </Link> },
        { header: 'Similar Item', accessor: 'similar_item_names', render: item => (item as Item).similar_item_names || '-' },
        { header: 'Item Code', accessor: 'itemCode' },
        { header: 'Item Category', accessor: 'item_category', render: item => (item as Item)?.item_category?.categoryName },
        { header: 'Stock', accessor: 'stock', render: item => <span className={Number((item as Item).total_stock_in - (item as Item).total_stock_out) <= 0 ? 'text-danger' : ''}>{Number((item as Item).total_stock_in - (item as Item).total_stock_out) ? numberFormat(Number((item as Item).total_stock_in - (item as Item).total_stock_out)) : 0}</span> },
        { header: 'Average Rate', accessor: 'average_rate', render: item => numberFormat((item as Item).average_rate) },
        { header: 'Latest Rate', accessor: 'latest_purchase_rate', render: item => numberFormat((item as Item).latest_purchase_rate) },
        { header: 'Unit', accessor: 'unit', render: item => (item as Item).unit?.unitName },
    ];

    // Define columns for products table
    const productColumns: Column[] = [
        { header: 'SL. No', accessor: 'index' },
        { header: 'Product Name', accessor: 'productName', render: item => <Link to={'/finished-goods-stock-details?id_products=' + (item as Product)?.id_products + '&id_projects=' + (selectedProject?.value ?? '')+'&id_warehouses='+(warehouse.value ?? '')} className="label label-info">
            {(item as Product).productName}
            <br></br>
			<span style={{ fontSize: '10px' }}>{(item as Product).alternateName}</span>
        </Link> },
        { header: 'Packing', accessor: 'packing' },
        { header: 'Size', accessor: 'size' },
        { header: 'Category', accessor: 'categoryName' },
        { header: 'Stock', accessor: 'stock', render: item => <span className={Number((item as Product).total_stock_in - (item as Product).total_stock_out) <= 0 ? 'text-danger' : ''}>{Number((item as Product).total_stock_in - (item as Product).total_stock_out) ? numberFormat(Number((item as Product).total_stock_in - (item as Product).total_stock_out)) : 0}</span> },
        { header: 'Average Rate', accessor: 'average_rate', render: item => numberFormat((item as Product).average_rate) },
        { header: 'Latest Rate', accessor: 'latest_production_rate', render: item => numberFormat((item as Product).latest_production_rate) },
        { header: 'Unit', accessor: 'unit', render: product => (product as Product)?.unit?.unitName },
    ];

    // Filter items based on zero stock preference
    const filteredItems = showZeroStock 
        ? items 
        : items.filter(item => Number(item.total_stock_in - item.total_stock_out) > 0);

    // Filter products based on zero stock preference
    const filteredProducts = showZeroStock 
        ? products 
        : products.filter(product => Number(product.total_stock_in - product.total_stock_out) > 0);


    useEffect(() => {
        getStockStatus()
    }, [getStockStatus])

    // Debounced effect for search text
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            getStockStatus();
        }, 500); // 500ms delay for search

        return () => clearTimeout(timeoutId);
    }, [searchText, selectedItemCategory, selectedProductCategory, getStockStatus]);

    const handleSelectProjectChange = (selectedOption: any) => {
        // Set the selected project state
        setSelectedProject(selectedOption);


        getWarehousesByProject(selectedOption.value);
        setWarehouse({ label: 'Select Warehouse', value: '' });
        // Fetch stock status for the selected project
        getStockStatus()
    };

    const handleWarehouseChange = (selected: any) => {
		setWarehouse(selected);
        getStockStatus()
	};

    const handleItemCategoryChange = (selected: any) => {
        setSelectedItemCategory(selected);
    };

    const handleProductCategoryChange = (selected: any) => {
        setSelectedProductCategory(selected);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const handleShowZeroStockChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowZeroStock(event.target.checked);
    };

    const handleReset = () => {
        setSearchText('');
        setSelectedItemCategory({ label: 'All Item Categories', value: '' });
        setSelectedProductCategory({ label: 'All Product Categories', value: '' });
        setSelectedProject(null);
        setWarehouse({ label: 'Select Warehouse', value: '' });
        setWarehouses([]);
        setShowZeroStock(true);
    };

    return (
        <>
            <div className='card card-custom'>
                <div className='card-header'>
                    <div className="card-title">
                        <KTSVG path='/media/svg/icons/Text/Bullet-list.svg' className='svg-icon-1 svg-icon-primary' />
                        <span className='text fw-bolder' style={{ marginLeft: '10px' }}>
                            Inventory Stock Status
                        </span>
                    </div>
                    <div className="card-toolbar">
                        <div className='d-flex justify-content-end align-items-center' style={{ gap: '12px', flexWrap: 'wrap' }}>
                            {/* Show Zero Stock Checkbox */}
                            <div className="form-check d-flex align-items-center" style={{ minWidth: '140px' }}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showZeroStock"
                                    checked={showZeroStock}
                                    onChange={handleShowZeroStockChange}
                                />
                                <label className="form-check-label ms-2" htmlFor="showZeroStock" style={{ fontSize: '14px' }}>
                                    Show Zero Stock
                                </label>
                            </div>

                            {/* Search Input */}
                            <div style={{ minWidth: '200px' }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search items/products..."
                                    value={searchText}
                                    onChange={handleSearchChange}
                                />
                            </div>
                            
                            {/* Item Category Dropdown */}
                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    value={selectedItemCategory}
                                    onChange={handleItemCategoryChange}
                                    options={itemCategories}
                                    placeholder="Select Item Category"
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                />
                            </div>

                            {/* Product Category Dropdown */}
                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    value={selectedProductCategory}
                                    onChange={handleProductCategoryChange}
                                    options={productCategories}
                                    placeholder="Select Product Category"
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                />
                            </div>

                            {/* Project Dropdown */}
                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    defaultValue={selectProjectOptions}
                                    onChange={handleSelectProjectChange}
                                    options={selectProjectOptions}
                                    placeholder="Select project"
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                />
                            </div>

                            {/* Warehouse Dropdown */}
                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    value={warehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouses}
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                    isDisabled={warehouses.length === 0}
                                />
                            </div>

                            {/* Reset Button */}
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={handleReset}
                                    title="Reset all filters"
                                    style={{ height: '31px' }}
                                >
                                    <i className='fas fa-undo'></i> Reset
                                </button>
                            </div>

                            {/* Excel Download Button */}
                            <div>
                                <a 
                                    className="btn btn-sm btn-primary" 
                                    href={`${process.env.REACT_APP_BACKEND}inventory_stock_status_download?id_projects=${selectedProject?.value ?? ''}&id_warehouses=${warehouse?.value ?? ''}&id_item_category=${selectedItemCategory?.value ?? ''}&id_product_category=${selectedProductCategory?.value ?? ''}&search=${searchText}`}
                                    style={{ height: '31px', display: 'inline-flex', alignItems: 'center' }}
                                >
                                    <i className='fas fa-download'></i> Excel Download
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='card-body'>
                    <Row>
                        <StockTable
                            data={filteredItems.map((item, index) => ({ ...item, index: index + 1 }))}
                            columns={itemColumns}
                            title="Item Stock"
                        />

                        <StockTable
                            data={filteredProducts.map((product, index) => ({ ...product, index: index + 1 }))}
                            columns={productColumns}
                            title="Finished Goods Stock"
                        />

                    </Row>

                </div>
            </div>

        </>
    )
}

export default InventoryStockStatus
